import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccessDeniedState from '../../components/AccessDeniedState';
import { getLoggedInUser, hasModulePermission } from '../../utils/permissions';
import { getDefaultWeekRange } from '../../utils/dateRange';

const EVENT_LOG_PERMISSION_KEYS = ['Event Logs', 'Event Log'];

const formatDateTime = (value) => {
  if (!value) return '-';

  try {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const actionToneMap = {
  Create: 'success',
  Update: 'warning',
  Delete: 'error',
};


const getChangedFields = (beforeData, afterData) => {
  const beforeObject = beforeData && typeof beforeData === 'object' ? beforeData : {};
  const afterObject = afterData && typeof afterData === 'object' ? afterData : {};
  const ignoredKeys = new Set([
    '_id',
    'created_datetime',
    'updated_datetime',
    'created_ip',
    'updated_ip',
    'before_data',
    'after_data',
  ]);

  return Array.from(new Set([...Object.keys(beforeObject), ...Object.keys(afterObject)]))
    .filter((key) => !ignoredKeys.has(key))
    .filter((key) => JSON.stringify(beforeObject[key]) !== JSON.stringify(afterObject[key]))
    .slice(0, 8);
};

const getActivitySummary = (log) => {
  const action = (log?.action || '').toLowerCase();
  const actor = log?.actor || 'Someone';
  const moduleName = log?.module || 'the module';
  const entityId = log?.entity_id ? `record ${log.entity_id}` : 'a record';

  if (action === 'create') return `${actor} created ${entityId} in ${moduleName}.`;
  if (action === 'update') return `${actor} modified ${entityId} in ${moduleName}.`;
  if (action === 'delete') return `${actor} deleted ${entityId} from ${moduleName}.`;

  return `${actor} performed ${log?.action || 'an action'} on ${entityId} in ${moduleName}.`;
};

const formatFieldLabel = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) {
    if (!value.length) return '-';
    return value.map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const renderRecordDetails = (record, emptyMessage) => {
  const entries = record && typeof record === 'object' ? Object.entries(record) : [];

  if (!entries.length) {
    return (
      <Typography color="text.secondary">
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <Stack spacing={1.25}>
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            border: '1px solid rgba(15, 23, 42, 0.08)',
            borderRadius: 2,
            px: 1.5,
            py: 1.25,
            bgcolor: '#fff',
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#52607a', mb: 0.5 }}>
            {formatFieldLabel(key)}
          </Typography>
          <Typography
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontFamily: 'inherit',
              fontSize: 13,
              color: '#0f172a',
            }}
          >
            {formatFieldValue(value)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

export default function EventLogsPage() {
  const [moduleOptions, setModuleOptions] = useState(['All Modules']);
  const [selectedModule, setSelectedModule] = useState('All Modules');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState(() => getDefaultWeekRange().fromDate);
  const [toDate, setToDate] = useState(() => getDefaultWeekRange().toDate);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedLog, setSelectedLog] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const currentUser = getLoggedInUser();
  const accessRole = currentUser?.accessRole || {};
  const canView = hasModulePermission(EVENT_LOG_PERMISSION_KEYS, 'View', accessRole);

  const fetchLogs = useCallback(async (moduleName = 'All Modules') => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${baseURL}/event_logs`, {
        params: { module: moduleName, from_date: fromDate, to_date: toDate },
      });

      const payload = response?.data || {};
      setModuleOptions(Array.isArray(payload.available_modules) ? payload.available_modules : ['All Modules']);
      setSelectedModule(payload.selected_module || moduleName);
      setRows(Array.isArray(payload.logs) ? payload.logs : []);
    } catch {
      setRows([]);
      setError('Unable to load event logs right now. Please check the API connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [baseURL, fromDate, toDate]);

  useEffect(() => {
    if (!canView) return;
    fetchLogs(selectedModule);
  }, [canView, fetchLogs, selectedModule]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      [
        row?.module,
        row?.action,
        row?.actor,
        row?.event_ip,
        row?.entity_id,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [rows, searchTerm]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const stats = useMemo(() => {
    const total = rows.length;
    const createCount = rows.filter((row) => row.action === 'Create').length;
    const updateCount = rows.filter((row) => row.action === 'Update').length;
    const deleteCount = rows.filter((row) => row.action === 'Delete').length;

    return [
      { label: 'Visible Logs', value: total, helper: selectedModule },
      { label: 'Created Events', value: createCount, helper: 'New records added' },
      { label: 'Updated Events', value: updateCount, helper: 'Existing records changed' },
      { label: 'Deleted Events', value: deleteCount, helper: 'Removed records' },
    ];
  }, [rows, selectedModule]);

  const handleModuleChange = async (event) => {
    const nextModule = event.target.value;
    setSelectedModule(nextModule);
    setPage(0);
  };

  if (!canView) {
    return <AccessDeniedState message="You do not have permission to view event logs." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 }, bgcolor: '#f4f7fb' }}>
      <Paper
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          mb: 3,
          color: '#fff',
          background: 'linear-gradient(135deg, #173971 0%, #2f6fd0 58%, #78a5ff 100%)',
          boxShadow: '0 24px 60px rgba(23, 57, 113, 0.18)',
        }}
      >
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={3}>
          <Box sx={{ maxWidth: 680 }}>
            <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.82 }}>
              OPERATIONS TIMELINE
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Event Logs
            </Typography>
            <Typography sx={{ opacity: 0.9 }}>
              Review all create, update, and delete activity from one place. Use the module selector to switch between a single workflow or a complete system view.
            </Typography>
          </Box>

          <Box sx={{ width: { xs: '100%', lg: 'auto' }, minWidth: { lg: 420 } }}>
            <Typography sx={{ mb: 0.75, fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.88)' }}>
              Module
            </Typography>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="stretch"
              justifyContent="flex-end"
              sx={{ width: '100%' }}
            >
              <FormControl
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 220,
                  '& .MuiOutlinedInput-root': {
                    height: 44,
                    bgcolor: 'rgba(255,255,255,0.16)',
                    color: '#fff',
                    borderRadius: 2.25,
                  },
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '44px',
                    py: 0,
                  },
                  '& .MuiSvgIcon-root': { color: '#fff' },
                }}
              >
                <Select
                  value={selectedModule}
                  onChange={handleModuleChange}
                >
                  {moduleOptions.map((moduleName) => (
                    <MenuItem key={moduleName} value={moduleName}>
                      {moduleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                onClick={() => fetchLogs(selectedModule)}
                size="small"
                sx={{
                  height: 44,
                  minHeight: 44,
                  px: 2.25,
                  borderRadius: 2.25,
                  borderColor: 'rgba(255,255,255,0.45)',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  backdropFilter: 'blur(8px)',
                  fontWeight: 600,
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    borderColor: '#fff',
                    bgcolor: 'rgba(255,255,255,0.18)',
                  },
                }}
              >
                Refresh
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((item) => (
          <Grid key={item.label} size={{ xs: 12, sm: 6, lg: 3 }}>
            <Paper sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>{item.label}</Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{item.value}</Typography>
              <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 13 }}>{item.helper}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>Activity Feed</Typography>
            <Typography color="text.secondary">
              Search by module, actor, action, entity ID, or IP address.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField size="small" type="date" label="From date" InputLabelProps={{ shrink: true }} value={fromDate} onChange={(event) => setFromDate(event.target.value)} inputProps={{ max: toDate || undefined }} />
          <TextField size="small" type="date" label="To date" InputLabelProps={{ shrink: true }} value={toDate} onChange={(event) => setToDate(event.target.value)} inputProps={{ min: fromDate || undefined }} />
          <TextField
            size="small"
            label="Search logs"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: { xs: '100%', md: 320 } }}
          />
          </Stack>
        </Stack>

        {error ? (
          <Typography sx={{ mb: 2, color: 'error.main' }}>{error}</Typography>
        ) : null}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Entity ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Event Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>IP Address</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Changes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>Loading event logs...</Box>
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ py: 5, textAlign: 'center', color: 'text.secondary' }}>
                      No event logs found for the selected module.
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((row) => (
                  <TableRow key={`${row.module}-${row.id}`} hover>
                    <TableCell>{row.module || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.action || 'Unknown'}
                        color={actionToneMap[row.action] || 'default'}
                        variant={actionToneMap[row.action] ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{row.actor || '-'}</TableCell>
                    <TableCell>{row.entity_id || '-'}</TableCell>
                    <TableCell>{formatDateTime(row.event_datetime)}</TableCell>
                    <TableCell>{row.event_ip || '-'}</TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => setSelectedLog(row)}>
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredRows.length}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} fullWidth maxWidth="md">
        <DialogTitle>Event Log Summary</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f7faff 0%, #eef4ff 100%)',
                border: '1px solid rgba(47, 111, 208, 0.14)',
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Avatar sx={{ bgcolor: '#173971', width: 52, height: 52 }}>
                  {(selectedLog?.actor || 'S').slice(0, 1).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {getActivitySummary(selectedLog)}
                  </Typography>
                  <Typography color="text.secondary">
                    Easy-to-read activity summary for operations and audit review.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} flexWrap="wrap">
              <Chip label={`Module: ${selectedLog?.module || '-'}`} />
              <Chip label={`Action: ${selectedLog?.action || '-'}`} color={actionToneMap[selectedLog?.action] || 'default'} />
              <Chip label={`By: ${selectedLog?.actor || '-'}`} variant="outlined" />
              <Chip label={`Record ID: ${selectedLog?.entity_id || '-'}`} variant="outlined" />
            </Stack>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.25 }}>Who</Typography>
                  <Typography variant="body1">{selectedLog?.actor || '-'}</Typography>
                  <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 13 }}>
                    Person who performed this action
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.25 }}>When</Typography>
                  <Typography variant="body1">{formatDateTime(selectedLog?.event_datetime)}</Typography>
                  <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 13 }}>
                    Date and time of the activity
                  </Typography>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Typography sx={{ fontWeight: 700, mb: 1.25 }}>IP Address</Typography>
                  <Typography variant="body1">{selectedLog?.event_ip || '-'}</Typography>
                  <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: 13 }}>
                    Network location used for this action
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1.5 }}>What Changed</Typography>
              {selectedLog?.action === 'Create' ? (
                <Typography color="text.secondary">
                  A new record was created in this module.
                </Typography>
              ) : selectedLog?.action === 'Delete' ? (
                <Typography color="text.secondary">
                  The selected record was deleted from this module.
                </Typography>
              ) : getChangedFields(selectedLog?.before_data, selectedLog?.after_data).length > 0 ? (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {getChangedFields(selectedLog?.before_data, selectedLog?.after_data).map((field) => (
                    <Tooltip key={field} title={`Field changed: ${formatFieldLabel(field)}`} arrow>
                      <Chip
                        label={formatFieldLabel(field)}
                        sx={{ bgcolor: '#eef4ff', color: '#173971', fontWeight: 600 }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  The record was updated successfully.
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fbfcfe' }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Plain Language Summary</Typography>
              <Typography color="text.secondary">
                {`This record was ${String(selectedLog?.action || 'updated').toLowerCase()} by ${selectedLog?.actor || '-'} on ${formatDateTime(selectedLog?.event_datetime)} from IP address ${selectedLog?.event_ip || '-'}.`}
              </Typography>
            </Paper>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#fff8f8', borderColor: 'rgba(204, 58, 58, 0.18)' }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.5, color: '#9f1239' }}>Old Record</Typography>
                  <Typography sx={{ color: 'text.secondary', mb: 1.5, fontSize: 13 }}>
                    Record values before this action happened.
                  </Typography>
                  {renderRecordDetails(
                    selectedLog?.before_data,
                    selectedLog?.action === 'Create'
                      ? 'No old record is available because this action created a new record.'
                      : 'No previous record details are available.'
                  )}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f7fff8', borderColor: 'rgba(22, 163, 74, 0.18)' }}>
                  <Typography sx={{ fontWeight: 700, mb: 0.5, color: '#166534' }}>New Record</Typography>
                  <Typography sx={{ color: 'text.secondary', mb: 1.5, fontSize: 13 }}>
                    Record values after this action happened.
                  </Typography>
                  {renderRecordDetails(
                    selectedLog?.after_data,
                    selectedLog?.action === 'Delete'
                      ? 'No new record is available because this action deleted the record.'
                      : 'No updated record details are available.'
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>Compact Audit Data</Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1.25, fontSize: 13 }}>
                Key audit information captured for this activity.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip label={`Module: ${selectedLog?.module || '-'}`} />
                <Chip label={`Action: ${selectedLog?.action || '-'}`} />
                <Chip label={`Actor: ${selectedLog?.actor || '-'}`} />
                <Chip label={`Date: ${formatDateTime(selectedLog?.event_datetime)}`} />
                <Chip label={`IP: ${selectedLog?.event_ip || '-'}`} />
              </Stack>
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
